import { COLORS } from '../../constants/theme';
import { formatNumber } from '../../utils/format';
import { Button } from '../ui/Button';
import { WarRoomTargetCard } from './WarRoomTargetCard';
import { WarRoomTroopControls } from './WarRoomTroopControls';
import { WarRoomOutcome, OwnOffenseCard } from './WarRoomOutcome';
import { DefenseLeftAtHomeCard } from './DefenseLeftAtHomeCard';
import { EstimatedGainPanel } from './EstimatedGainPanel';
import { ConfirmAttackDialog } from './ConfirmAttackDialog';
import { buildSuccessLabel, renderTravelModifiers } from '../../hooks/useWarRoomState';
import type { WarRoomShellProps } from '../../hooks/useWarRoomState';

/**
 * SF-147 Component 1 (redesigned SF-174 Component 1): desktop War Room layout. `<h1>` now lives
 * at page level (`Attack.tsx`). Keeps the 2-column grid (troop controls left, outcome column
 * right); the right column gains two new sibling stat cards (Component 6).
 */
export function DesktopWarRoomShell({
  province,
  targetMember,
  intel,
  onRunSohd,
  mode,
  onSwitchToAuto,
  onSwitchToManual,
  autoSwitchReason,
  generals,
  oversendPct,
  composition,
  includeSoldiers,
  includeMercenaries,
  targetOP,
  onGeneralsChange,
  onOversendChange,
  onFieldChange,
  onToggleSoldiers,
  onToggleMercenaries,
  outcome,
  attackOP,
  targetTD,
  isStale,
  isMissingIntel,
  isImpossible,
  onSendEverything,
  ownNetworth,
  militaryEffectPercentage,
  isOwnKingdomAtWar,
  ownStance,
  targetStance,
  targetDragonType,
  travelTime,
  selectedAttackType,
  isSubmitting,
  submitError,
  canLaunch,
  onLaunchClick,
  mercenaryTotalCost,
  showConfirmDialog,
  onConfirmAttack,
  onCancelConfirm,
  race,
  unitPool,
}: WarRoomShellProps) {
  const projectedLose = outcome === 'Lose';
  const targetName = targetMember ? `${targetMember.provinceName} (${targetMember.location})` : '';

  return (
    <div className="space-y-3">
      <WarRoomTargetCard
        targetMember={targetMember}
        intel={intel}
        onRunSohd={onRunSohd}
        currentTick={province.sync.currentTick}
        ownNetworth={ownNetworth}
        selectedAttackType={selectedAttackType}
      />

      <div className="grid grid-cols-2 gap-3" style={{ alignItems: 'start' }}>
        <WarRoomTroopControls
          province={province}
          unitPool={unitPool}
          mode={mode}
          onSwitchToAuto={onSwitchToAuto}
          onSwitchToManual={onSwitchToManual}
          autoSwitchReason={autoSwitchReason}
          isImpossible={isImpossible}
          verdict={outcome}
          generals={generals}
          oversendPct={oversendPct}
          composition={composition}
          includeSoldiers={includeSoldiers}
          includeMercenaries={includeMercenaries}
          targetOP={targetOP}
          onGeneralsChange={onGeneralsChange}
          onOversendChange={onOversendChange}
          onFieldChange={onFieldChange}
          onToggleSoldiers={onToggleSoldiers}
          onToggleMercenaries={onToggleMercenaries}
        />

        <div className="space-y-3">
          <OwnOffenseCard attackOP={attackOP} />
          <DefenseLeftAtHomeCard province={province} composition={composition} />
          <WarRoomOutcome
            outcome={outcome}
            attackOP={attackOP}
            targetTD={targetTD}
            isStale={isStale}
            isMissingIntel={isMissingIntel}
            isImpossible={isImpossible}
            onSendEverything={onSendEverything}
          />
          <EstimatedGainPanel
            selectedAttackType={selectedAttackType}
            verdict={outcome}
            targetMember={targetMember}
            ownNetworth={ownNetworth}
            ownAcres={province.territory.totals.acres}
            attackOP={attackOP}
            targetTD={targetTD}
            militaryEffectPercentage={militaryEffectPercentage}
            isOwnKingdomAtWar={isOwnKingdomAtWar}
            ownStance={ownStance}
            targetStance={targetStance}
            targetDragonType={targetDragonType}
            intel={intel}
          />
        </div>
      </div>

      {/* Commit bar — attack type moved to page-level AttackTypeSelector (Component 3) */}
      <div
        className="rounded px-4 py-3 space-y-2"
        style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}
      >
        {submitError && (
          <div className="text-sm" style={{ color: COLORS.negative }}>{submitError}</div>
        )}
        {projectedLose && (
          <div className="text-sm" style={{ color: COLORS.warning }}>
            You are projected to lose this fight.
          </div>
        )}
        <div className="flex items-center gap-3">
          <div className="flex flex-col">
            <span className="text-sm font-medium" style={{ color: COLORS.text.primary }}>
              Army returns in {travelTime.finalTime} days
            </span>
            <span className="text-xs" style={{ color: COLORS.text.muted }}>
              {renderTravelModifiers(travelTime)}
            </span>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Button
              onClick={onLaunchClick}
              disabled={!canLaunch}
              isLoading={isSubmitting}
            >
              Launch Attack
            </Button>
          </div>
        </div>
        {mercenaryTotalCost > 0 && (
          <div className="text-xs" style={{ color: COLORS.warning }}>
            Mercenary cost: {formatNumber(mercenaryTotalCost)} gc
          </div>
        )}
      </div>

      <ConfirmAttackDialog
        isOpen={showConfirmDialog}
        onConfirm={onConfirmAttack}
        onCancel={onCancelConfirm}
        attackType={selectedAttackType}
        targetName={targetName}
        armyComposition={composition}
        modifiedOffense={attackOP}
        travelTime={travelTime}
        modifiedDefense={targetTD}
        successLabel={buildSuccessLabel(outcome)}
        mercenaryTotalCost={mercenaryTotalCost}
        race={race}
      />
    </div>
  );
}
